import * as React from "react";
import * as ReactDOM from "react-dom";
import { ErrorBoundary } from "react-error-boundary";
import { createSafeLazy} from "react-safe-lazy";
import { tl } from '../utils';

const safeLazy = createSafeLazy({
  importRetries: 3,
  forceReload: {
    maxRetries: 2,
  },
});

const Login = safeLazy(() => import("./login/index"));
const Welcome = safeLazy(() => import("./panel/WelcomePage/index"));
const Courses = safeLazy(() => import("./panel/Courses/index"));
const CourseSideBanner = safeLazy(() => import("../components/CourseSideBanner/index"));
const Lessons = safeLazy(() => import("./panel/Lessons/index"));
const AnswerTemplates = safeLazy(() => import("./panel/AnswerTemplates/index"));
const News = safeLazy(() => import("./panel/News/index"));
const ATPay = safeLazy(() => import("./atPay/manage/index"));
const ServiceToggle = safeLazy(() => import("./panel/ServiceToggle/index"));
const GroupChatSettings = safeLazy(() => import("./panel/Chats/Group/index"));
const TelegramChats = safeLazy(() => import("./panel/Chats/Telegram/index"));
const TelegramAlert = safeLazy(() => import("./panel/Chats/Telegram/alert"));
const PlanInfo = safeLazy(() => import("./panel/PlanInfo/index"));
const ActPickDialog = safeLazy(() => import("./panel/DocumentTemplates/ActPickDialog"));
const DocumentTemplates = safeLazy(() => import("./panel/DocumentTemplates/index"));
const AutoDelivery = safeLazy(() => import("./panel/Autodelivery/index"));
const MailsList = safeLazy(() => import("./panel/Distribution/index"));
const HWByCurators = safeLazy(() => import("./panel/Homeworks/ByCurators/index"));
const HWByLessons = safeLazy(() => import("./panel/Homeworks/ByLessons/index"));
const Title = safeLazy(() => import("./panel/ReactTitle/index"));
const Branding = safeLazy(() => import("./panel/Branding/index"));
const Tags = safeLazy(() => import("./panel/Tags/index"));
const ContactAuthorPopup = safeLazy(() => import("./panel/Popup/ContactAuthor/index"));
const SmsFee = safeLazy(() => import("../components/Modals/smsNotificationFee"));
const StudentsGroups = safeLazy(() => import("./panel/StudentGroups/index"));
const PointsPayCompletePopup = safeLazy(() => import("./panel/Popup/Plans/PointsPayComplete/index"));
const ConfirmLeavePagePopup = safeLazy(() => import("./panel/Popup/ConfirmLeavePage/index"));
const MobileAppDialog = safeLazy(() => import("./panel/MobileAppDialog/index"));
const LessonsCopy = safeLazy(() => import("./panel/Courses/Copy/index"));
const AddStudentsModal = safeLazy(() => import("./panel/Courses/Students/AddStudentsModal/index"));
const CharacterSelect = safeLazy(() => import("./panel/CharacterSelect/index"));
const AchievementsNotifications = safeLazy(() => import("./panel/Achievements/index"));
const PageNotification = safeLazy(() => import("./panel/Notification/index"));
const InfoProtector = safeLazy(() => import("./panel/Courses/Infoprotector/index"));
const PreviewCourse = safeLazy(() => import("./panel/Courses/Preview/index"));
const WhiteLabel = safeLazy(() => import("./panel/UserCabinet/Whitelabel/index"));
const Profile = safeLazy(() => import("./panel/Profile/index"));
const FileStorage = safeLazy(() => import("./panel/Filestorage/index"));
const SmartWebinarReminders = safeLazy(() => import("./panel/lesson/Reminders"));
const LessonImageHeader = safeLazy(() => import("./panel/lesson/LessonImageHeader"));
const DayCalendar = safeLazy(() => import("./panel/diary/daycalendar"));
const WeekCalendar = safeLazy(() => import("./panel/diary/weekcalendar"));
const MonthCalendar = safeLazy(() => import("./panel/diary/monthcalendar"));
const HomeworkCheck = safeLazy(() => import("./panel/homeworkCheck/index"));
const HomeworkHistory = safeLazy(() => import("./panel/homeworkHistory/index"));
const Certificate = safeLazy(() => import("./panel/Certificate/index"));
const CertificatePage = safeLazy(() => import("./panel/certificates/index"));
const CertificateCheckPage = safeLazy(() => import("./panel/certificates/CheckPage"));
const CuratorStudents = safeLazy(() => import("./panel/curators/CuratorStudents/index"));
const CuratorPosition = safeLazy(() => import("./panel/curators/CuratorPosition/index"));
const Curators = safeLazy(() => import("./panel/curators/index"));
const Contact = safeLazy(() => import("./panel/contact/index"));
const Scope = safeLazy(() => import("./panel/scope/index"));
const GameGroup = safeLazy(() => import("./panel/game/tasks_group/index"));
const Game = safeLazy(() => import("./panel/game/tasks/index"));
const AnswerForm = safeLazy(() => import("../components/AnswerForm/index"));
const Pirates = safeLazy(() => import("./panel/Profile/Pirates"));
const StudentRecurrings = safeLazy(() => import("./panel/StudentRecurrings/index"));
const Proctoring = safeLazy(() => import("./panel/Proctoring/index"));
const Mailings = safeLazy(() => import("./panel/Mailings/index"));
const FilestorageSettings = safeLazy(() => import("./panel/Filestorage/Settings/index"));
const FilestorageSettingsWidget = safeLazy(() => import("./panel/Filestorage/Settings/widget"));
const AuthMissing = safeLazy(() => import("./panel/AuthMissing/index"));
const HeaderBanner = safeLazy(() => import("../components/HeaderBanner/index"));
const TranscriptionPromoTemplate = safeLazy(() => import("./panel/Transcriptions/TranscriptionPromoTemplate/index"));
const TranscriptionOpenTemplate = safeLazy(() => import("./panel/Transcriptions/TranscriptionOpenTemplate/index"));
const TranscriptionBadgeTemplate = safeLazy(() => import("./panel/Transcriptions/TranscriptionBadgeTemplate/index"));
const TranscriptionList = safeLazy(() => import("./panel/Transcriptions/TranscriptionList/index"));
const TranscriptionEdit = safeLazy(() => import("./panel/Transcriptions/TranscriptionEdit/index"));
const TranscriptionStudent = safeLazy(() => import("./panel/Transcriptions/TranscriptionStudent/index"));
const AIAssistant = safeLazy(() => import("./panel/AI/assistant"));
const AITest = safeLazy(() => import("./panel/AI/test"));
const TagsCard = safeLazy(() => import("./panel/MUI/TagsCard/index"));

export type TEmbedComponent =
  "login" |
  "welcome" |
  "lessons" | 
  "courses" |
  "courseSideBanner" |
  "answerTemplates" |
  "news" |
  "atpay" |
  "serviceToggle" |
  "groupChatSettings" |
  "telegramChats" |
  "telegramAlert" |
  "planInfo" |
  "actPickDialog" |
  "documentTemplates" |
  "autoDelivery" |
  "mailsList" |
  "hwByCurators" |
  "hwByLessons" |
  "title" |
  "branding" |
  "tags" |
  "contactAuthorPopup" |
  "smsFee" |
  "studentsGroups" |
  "pointsPayCompletePopup" |
  "confirmLeavePagePopup" |
  "mobileAppDialog" |
  "lessonsCopy" |
  "addStudentsModal" |
  "characterSelect" |
  "achievementsNotifications" |
  "pageNotification" |
  "infoProtector" |
  "previewCourse" |
  "whiteLabel" |
  "profile" |
  "fileStorage" |
  "smartWebinarReminders" |
  "lessonImageHeader" |
  "dayCalendar" |
  "weekCalendar" |
  "monthCalendar" |
  "homeworkCheck" |
  "homeworkHistory" |
  "certificate" |
  "certificatePage" |
  "certificateCheckPage" |
  "curatorStudents" |
  "curatorPosition" |
  "curators" |
  "contact" |
  "scope" |
  "gameGroup" |
  "game" | 
  "answerForm" |
  "pirates" |
  "studentRecurrings" |
  "proctoring" |
  "mailings" |
  "authMissing" |
  "filestorageSettings" |
  "filestorageSettingsWidget" |
  "headerBanner" |
  "aiAssistant" |
  "aiTest" |
  "transcriptionOpenTemplate" |
  "transcriptionPromoTemplate" |
  "transcriptionBadgeTemplate" |
  "transcriptionList" |
  "transcriptionEdit" |
  "transcriptionStudent" |
  "tagsCard"
;
 
const componentsWithoutFallback: Partial<TEmbedComponent>[] = [
  "serviceToggle",
  "courseSideBanner",
  "telegramAlert",
  "actPickDialog",
  "title",
  "contactAuthorPopup",
  "smsFee",
  "pointsPayCompletePopup",
  "confirmLeavePagePopup",
  "addStudentsModal",
  "characterSelect",
  "achievementsNotifications",
  "pageNotification",
  "smartWebinarReminders",
  "filestorageSettingsWidget",
  "headerBanner"
]

interface RenderFactoryProps {
  component: TEmbedComponent;
  data: any;
}

const RenderFactory:React.FC<RenderFactoryProps> = ({
  component,
  data
}) => {
  switch (component) {
    case "login":
      return <Login {...data} />
    case "welcome":
      return <Welcome {...data} />
    case "courses":
      return <Courses {...data} />
    case "courseSideBanner":
      return <CourseSideBanner {...data} />
    case "lessons":
      return <Lessons {...data} />
    case "answerTemplates":
      return <AnswerTemplates {...data} />
    case "news":
      return <News {...data} />
    case "atpay":
      return <ATPay {...data} />
    case "serviceToggle":
      return <ServiceToggle {...data} />
    case "groupChatSettings":
      return <GroupChatSettings {...data} />
    case "telegramChats":
      return <TelegramChats {...data} />
    case "telegramAlert":
      return <TelegramAlert {...data} />
    case "planInfo":
      return <PlanInfo {...data} />
    case "actPickDialog":
      return <ActPickDialog {...data} />
    case "documentTemplates":
      return <DocumentTemplates {...data} />
    case "autoDelivery":
      return <AutoDelivery {...data} />
    case "mailsList":
      return <MailsList {...data} />
    case "hwByCurators":
      return <HWByCurators {...data} />
    case "hwByLessons":
      return <HWByLessons {...data} />
    case "title":
      return <Title {...data} />
    case "branding":
      return <Branding {...data} />
    case "tags":
      return <Tags {...data} />
    case "contactAuthorPopup":
      return <ContactAuthorPopup {...data} />
    case "smsFee":
      return <SmsFee {...data} />
    case "studentsGroups":
      return <StudentsGroups {...data} />
    case "pointsPayCompletePopup":
      return <PointsPayCompletePopup {...data} />
    case "confirmLeavePagePopup":
      return <ConfirmLeavePagePopup {...data} />
    case "mobileAppDialog":
      return <MobileAppDialog {...data} />
    case "lessonsCopy":
      return <LessonsCopy {...data} />
    case "addStudentsModal":
      return <AddStudentsModal {...data} />
    case "characterSelect":
      return <CharacterSelect {...data} />
    case "achievementsNotifications":
      return <AchievementsNotifications {...data} />
    case "pageNotification":
      return <PageNotification {...data} />
    case "infoProtector":
      return <InfoProtector {...data} />
    case "previewCourse":
      return <PreviewCourse {...data} />
    case "whiteLabel":
      return <WhiteLabel {...data} />
    case "profile":
      return <Profile {...data} />
    case "fileStorage":
      return <FileStorage {...data} />
    case "smartWebinarReminders":
      return <SmartWebinarReminders {...data} />
    case "lessonImageHeader":
      return <LessonImageHeader {...data} />
    case "dayCalendar":
      return <DayCalendar {...data} />
    case "weekCalendar":
      return <WeekCalendar {...data} />
    case "monthCalendar":
      return <MonthCalendar {...data} />
    case "homeworkCheck":
      return <HomeworkCheck {...data} />
    case "homeworkHistory":
      return <HomeworkHistory {...data} />
    case "certificate":
      return <Certificate {...data} />
    case "certificatePage":
      return <CertificatePage {...data} />
    case "certificateCheckPage":
      return <CertificateCheckPage {...data} />
    case "curatorStudents":
      return <CuratorStudents {...data} />
    case "curatorPosition":
      return <CuratorPosition {...data} />
    case "curators":
      return <Curators {...data} />
    case "contact":
      return <Contact {...data} />
    case "scope":
      return <Scope {...data} />
    case "gameGroup":
      return <GameGroup {...data} />
    case "game":
      return <Game {...data} />
    case "answerForm":
      return <AnswerForm {...data} />
    case "pirates":
      return <Pirates {...data} />
    case "studentRecurrings":
      return <StudentRecurrings {...data} />
    case "proctoring":
      return <Proctoring {...data} />
    case "mailings":
      return <Mailings {...data} />
    case "authMissing":
      return <AuthMissing {...data} />
    case "filestorageSettings":
      return <FilestorageSettings {...data} />
    case "filestorageSettingsWidget":
      return <FilestorageSettingsWidget {...data} />
    case "headerBanner":
      return <HeaderBanner {...data} />
    case "transcriptionPromoTemplate":
      return <TranscriptionPromoTemplate {...data} />
    case "transcriptionOpenTemplate":
      return <TranscriptionOpenTemplate {...data} />
    case "transcriptionBadgeTemplate":
      return <TranscriptionBadgeTemplate {...data} />
    case "transcriptionList":
      return <TranscriptionList {...data} />
    case "transcriptionEdit":
      return <TranscriptionEdit {...data} />
    case "transcriptionStudent":
      return <TranscriptionStudent {...data} />
    case "aiAssistant":
      return <AIAssistant {...data} />
    case "aiTest":
      return <AITest {...data} />
    case "tagsCard":
      return <TagsCard {...data} />
    default:
      return null;
  }
};

const Fallback: React.FC<{ error: Error, component: TEmbedComponent, nodeId: string }> = ({ error, component, nodeId }) => {
  return (
    <>
      {error.stack.includes("Chunk") &&
        <div role="alert">
          <p style={{ textAlign: "center", fontSize: 24, fontWeight: 700, marginBottom: 16 }}>{tl('Появилась новая версия')}</p>
          <p style={{ textAlign: "center", fontSize: 14, marginBottom: 16 }}>{tl('Подождите немного и перезагрузите страницу')}</p>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <button style={{ backgroundColor: "white", padding: "10px 20px", cursor: "pointer" }} onClick={() => window.location.reload()}>{tl('Перезагрузить страницу')}</button>
          </div>
        </div>
      }
      {!error.stack.includes("Chunk") &&
        <div role="alert">
          <p style={{ textAlign: "center", fontSize: 24, fontWeight: 700, marginBottom: 16 }}>{tl('Что-то пошло не так. Свяжитесь с нашей поддержкой и мы обязательно во всем разберемся')}</p>
          <p style={{ textAlign: "center", fontSize: 14, marginBottom: 16 }}>{tl('А если вы приложите текст ниже к обращению, то мы будем вам особенно благодарны')}</p>
          <div style={{ backgroundColor: "white", padding: 12, marginBottom: 16 }}>
            <pre style={{ color: "red" }}>{`Component: ${component}`}</pre>
            <pre style={{ color: "red" }}>{`NodeId: ${nodeId}`}</pre>
            <pre style={{ color: "red" }}>{error.stack}</pre>
          </div>
        </div>
      }
    </>
  )
}

const preloader = (
  <div style={{ textAlign: 'center' }}>
    {window._isMobileApp ? tl('Загрузка...') :
		  <iframe width="48px" height="48px" style={{ border: '0' }} src="data:image/svg+xml;utf8,<svg width='48' height='48' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'><circle cx='12' cy='3' r='0'><animate id='spinner_6RAU' begin='0;spinner_GErc.end-0.5s' attributeName='r' calcMode='spline' dur='0.6s' values='0;2;0' keySplines='.27,.42,.37,.99;.53,0,.61,.73'/></circle><circle cx='16.50' cy='4.21' r='0'><animate id='spinner_khXL' begin='spinner_6RAU.begin+0.1s' attributeName='r' calcMode='spline' dur='0.6s' values='0;2;0' keySplines='.27,.42,.37,.99;.53,0,.61,.73'/></circle><circle cx='7.50' cy='4.21' r='0'><animate id='spinner_GErc' begin='spinner_JEaM.begin+0.1s' attributeName='r' calcMode='spline' dur='0.6s' values='0;2;0' keySplines='.27,.42,.37,.99;.53,0,.61,.73'/></circle><circle cx='19.79' cy='7.50' r='0'><animate id='spinner_9orP' begin='spinner_khXL.begin+0.1s' attributeName='r' calcMode='spline' dur='0.6s' values='0;2;0' keySplines='.27,.42,.37,.99;.53,0,.61,.73'/></circle><circle cx='4.21' cy='7.50' r='0'><animate id='spinner_JEaM' begin='spinner_RwRf.begin+0.1s' attributeName='r' calcMode='spline' dur='0.6s' values='0;2;0' keySplines='.27,.42,.37,.99;.53,0,.61,.73'/></circle><circle cx='21.00' cy='12.00' r='0'><animate id='spinner_W8J5' begin='spinner_9orP.begin+0.1s' attributeName='r' calcMode='spline' dur='0.6s' values='0;2;0' keySplines='.27,.42,.37,.99;.53,0,.61,.73'/></circle><circle cx='3.00' cy='12.00' r='0'><animate id='spinner_RwRf' begin='spinner_tByH.begin+0.1s' attributeName='r' calcMode='spline' dur='0.6s' values='0;2;0' keySplines='.27,.42,.37,.99;.53,0,.61,.73'/></circle><circle cx='19.79' cy='16.50' r='0'><animate id='spinner_tedm' begin='spinner_W8J5.begin+0.1s' attributeName='r' calcMode='spline' dur='0.6s' values='0;2;0' keySplines='.27,.42,.37,.99;.53,0,.61,.73'/></circle><circle cx='4.21' cy='16.50' r='0'><animate id='spinner_tByH' begin='spinner_c3Lr.begin+0.1s' attributeName='r' calcMode='spline' dur='0.6s' values='0;2;0' keySplines='.27,.42,.37,.99;.53,0,.61,.73'/></circle><circle cx='16.50' cy='19.79' r='0'><animate id='spinner_QxRo' begin='spinner_tedm.begin+0.1s' attributeName='r' calcMode='spline' dur='0.6s' values='0;2;0' keySplines='.27,.42,.37,.99;.53,0,.61,.73'/></circle><circle cx='7.50' cy='19.79' r='0'><animate id='spinner_c3Lr' begin='spinner_PW3C.begin+0.1s' attributeName='r' calcMode='spline' dur='0.6s' values='0;2;0' keySplines='.27,.42,.37,.99;.53,0,.61,.73'/></circle><circle cx='12' cy='21' r='0'><animate id='spinner_PW3C' begin='spinner_QxRo.begin+0.1s' attributeName='r' calcMode='spline' dur='0.6s' values='0;2;0' keySplines='.27,.42,.37,.99;.53,0,.61,.73'/></circle></svg>"></iframe>
    }
	</div>
)

const load_react = (nodeId: string, component: TEmbedComponent, data: any) => {
  ReactDOM.render(
    <ErrorBoundary fallbackRender={(props) => <Fallback component={component} nodeId={nodeId} {...props} />}>
      <React.Suspense fallback={componentsWithoutFallback.includes(component) ? null : preloader}>
        <RenderFactory component={component} data={data} />
      </React.Suspense>
    </ErrorBoundary>,
    document.getElementById(nodeId)
  )
}

window.load_react = load_react;